<template>
  <div v-if="pack">
    <v-text-field
      v-model="search"
      append-icon="mdi-magnify"
      label="Search..."
      outlined
      dense
      clearable
      :hint="searchHelpText"
      persistent-hint
      color="primary"
      class="mt-4 mb-6"
      style="max-width: 500px"
      :aria-label="searchHelpText"
      data-cy="service-area-search"
    ></v-text-field>
    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :loading="loading"
      loading-text="Loading service areas..."
      must-sort
      hide-default-footer
      disable-pagination
    >
      <template v-slot:header.id="{ header }">
        <span data-cy="service-area-id-header">{{ header.text }}</span>
      </template>
      <template v-slot:item="{ item }">
        <tr>
          <td class="text-center" data-cy="service-area-number">
            <router-link :to="`/pack/${$route.params.packId}/dashboard/uat/${item.id}`">{{ item.id }}</router-link>
          </td>
          <td class="text-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon
                  @click="showEditForm(item)"
                  @keyup.enter="showEditForm(item)"
                  @keyup.space="showEditForm(item)"
                  color="primary"
                  class="edit-icon"
                  small
                  v-on="on"
                  role="button"
                  tabindex="0"
                  data-cy="edit-service-area-icon"
                  :aria-label="`Edit service area ${item.id}: ${item.name}`"
                >
                  fas fa-edit
                </v-icon>
              </template>
              <span>Edit account manager</span>
            </v-tooltip>
          </td>
          <td data-cy="service-area-name">{{ item.name }}</td>
          <td :class="`font-weight-bold och-statusCalculation__class--${getStatus(item)}`">
            {{ getStatusLabel(item) }}
          </td>
          <td data-cy="account-manager-cell">
            <div
              v-if="item.accountManager"
              :class="{ 'grey--text text--darken-2 font-italic': item.accountManager.active === 0 }"
            >
              <div>
                {{ item.accountManager.firstName }} {{ item.accountManager.lastName
                }}{{ item.accountManager.active === 0 ? " - Inactive" : "" }}
              </div>
              <div v-if="mailtoLink(item.accountManager.email)">
                <a :href="mailtoLink(item.accountManager.email)">{{ item.accountManager.email }}</a>
              </div>
            </div>
          </td>
          <td>
            <div
              v-if="item.contactPrimary"
              :class="{ 'grey--text text--darken-2 font-italic': item.contactPrimary.active === 0 }"
            >
              <div>
                {{ item.contactPrimary.firstName }} {{ item.contactPrimary.lastName
                }}{{ item.contactPrimary.active === 0 ? " - Inactive" : "" }}
              </div>
              <div v-if="mailtoLink(item.contactPrimary.email)">
                <a :href="mailtoLink(item.contactPrimary.email)">{{ item.contactPrimary.email }}</a>
              </div>
              <div>{{ item.contactPrimary.phone }}</div>
            </div>
          </td>
          <td>
            <div
              v-if="item.contactSecondary"
              :class="{ 'grey--text text--darken-2 font-italic': item.contactSecondary.active === 0 }"
            >
              <div>
                {{ item.contactSecondary.firstName }} {{ item.contactSecondary.lastName
                }}{{ item.contactSecondary.active === 0 ? " - Inactive" : "" }}
              </div>
              <div v-if="mailtoLink(item.contactSecondary.email)">
                <a :href="mailtoLink(item.contactSecondary.email)">{{ item.contactSecondary.email }}</a>
              </div>
              <div>{{ item.contactSecondary.phone }}</div>
            </div>
          </td>
          <td data-cy="member-admins">
            <div style="padding: 12px 0">
              <ul
                v-if="item.memberAdminList && item.memberAdminList.total > 0"
                class="pl-0"
                style="list-style: none; margin: 0; padding: 0"
              >
                <li
                  v-for="user in item.memberAdminList.active"
                  :key="user.username"
                  class="mb-3"
                  data-cy="member-admin-active"
                >
                  <div>{{ user.firstName }} {{ user.lastName }}</div>
                  <div v-if="mailtoLink(user.email)">
                    <a :href="mailtoLink(user.email)">{{ user.email }}</a>
                  </div>
                </li>
                <li
                  v-for="user in item.memberAdminList.inactive"
                  :key="user.username"
                  class="grey--text text--darken-2 font-italic mb-3"
                  data-cy="member-admin-inactive"
                >
                  <div>{{ user.firstName }} {{ user.lastName }} - Inactive</div>
                  <div v-if="mailtoLink(user.email)">
                    <a :href="mailtoLink(user.email)">{{ user.email }}</a>
                  </div>
                </li>
              </ul>
            </div>
          </td>
          <td data-cy="implement-later-count">{{ item.implementLaterCount }}</td>
          <td data-cy="not-impacted-count">{{ item.notImpactedCount }}</td>
          <td data-cy="unassigned-count">{{ item.unassignedCount }}</td>
        </tr>
      </template>
      <template v-slot:no-data>
        <div class="text-center pa-4">
          <div v-if="search">
            No service areas match your search for "<strong>{{ search }}</strong
            >".
          </div>
          <div v-else>No service areas found</div>
        </div>
      </template>
    </v-data-table>
    <account-manager-service-area-form
      v-model="showEdit"
      :service-area-id="editServiceAreaId"
    ></account-manager-service-area-form>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

import uatUtil from "@/lib/uatUtil";
import AccountManagerServiceAreaForm from "./AccountManagerServiceAreaForm";

export default {
  components: {
    AccountManagerServiceAreaForm,
  },
  data() {
    return {
      search: "",
      showEdit: false,
      editServiceAreaId: null,
      headers: [
        { text: "ID", value: "id", align: "center" },
        { text: "Edit", value: "edit", sortable: false, width: "50px", align: "center" },
        { text: "Name", value: "name" },
        { text: "Status", value: "statusCalculation" },
        { text: "Account Manager", value: "accountManager.lastName" },
        { text: "Primary Contact", value: "contactPrimary.lastName" },
        { text: "Secondary Contact", value: "contactSecondary.lastName" },
        { text: "Member Admins", value: "memberAdminList.total", sortable: false },
        { text: "# of Implement Later", value: "implementLaterCount" },
        { text: "# of Not Impacted", value: "notImpactedCount" },
        { text: "# of Unassigned", value: "unassignedCount" },
      ],
    };
  },
  computed: {
    pack() {
      const packIdRouteParam = this.packIdRouteParam;

      return this.packList.find(pack => pack.id === packIdRouteParam);
    },
    packIdRouteParam() {
      return parseInt(this.$route.params.packId, 10);
    },
    searchHelpText() {
      return "Search by ID, name, account manager, contacts, or member admins";
    },
    filteredItems() {
      if (!this.search) {
        return this.items;
      }

      const searchLower = this.search.toLowerCase();

      return this.items.filter(item => {
        // Search by Service Area ID or Name
        if (String(item.id).includes(searchLower)) return true;
        if (item.name && item.name.toLowerCase().includes(searchLower)) return true;

        // Search by Account Manager
        if (this.matchesUser(item.accountManager, searchLower)) return true;

        // Search by Primary Contact
        if (this.matchesUser(item.contactPrimary, searchLower)) return true;

        // Search by Secondary Contact
        if (this.matchesUser(item.contactSecondary, searchLower)) return true;

        // Search by Member Admins
        if (item.memberAdminList) {
          // Search active member admins
          if (
            item.memberAdminList.active &&
            item.memberAdminList.active.some(admin => this.matchesUser(admin, searchLower))
          ) {
            return true;
          }
          // Search inactive member admins
          if (
            item.memberAdminList.inactive &&
            item.memberAdminList.inactive.some(admin => this.matchesUser(admin, searchLower))
          ) {
            return true;
          }
        }

        return false;
      });
    },
    ...mapState({
      packList: state => state.pack.packList,
    }),
  },
  async created() {
    await this.getPackList();
  },
  methods: {
    mailtoLink(email) {
      // Validate email format before creating mailto link (defense-in-depth)
      if (!email || typeof email !== "string") return null;

      // Simple email validation: ensures basic structure (local@domain.tld) without whitespace
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) return null;

      return `mailto:${email}`;
    },
    matchesUser(user, searchLower) {
      if (!user) return false;

      const firstName = (user.firstName || "").toLowerCase();
      const lastName = (user.lastName || "").toLowerCase();
      const email = (user.email || "").toLowerCase();
      const username = (user.username || "").toLowerCase();
      const fullName = `${firstName} ${lastName}`.trim();

      return (
        firstName.includes(searchLower) ||
        lastName.includes(searchLower) ||
        fullName.includes(searchLower) ||
        email.includes(searchLower) ||
        username.includes(searchLower)
      );
    },
    getStatus(serviceArea) {
      const { completion } = serviceArea;
      const { uatEnd, uatStart } = this.pack;

      const pack = Object.assign(
        {},
        {
          completion,
          uatEnd,
          uatStart,
        }
      );

      return uatUtil.getStatus(pack);
    },
    getStatusLabel(serviceArea) {
      const { completion } = serviceArea;
      const { uatEnd, uatStart } = this.pack;

      const pack = Object.assign(
        {},
        {
          completion,
          uatEnd,
          uatStart,
        }
      );

      return uatUtil.getStatusLabel(pack);
    },
    showEditForm(serviceArea) {
      this.editServiceAreaId = serviceArea.id;
      this.showEdit = true;
    },
    ...mapActions({
      getPackList: "pack/getList",
    }),
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style>
.och-statusCalculation__class--1 {
  background-color: #32a025;
  color: #fff;
}

.och-statusCalculation__class--2 {
  background-color: #a331bb;
  color: #fff;
}

.och-statusCalculation__class--3 {
  background-color: #f57f17;
  color: #fff;
}

.och-statusCalculation__class--4 {
  background-color: #de2200;
  color: #fff;
}

.edit-icon {
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.edit-icon:hover {
  transform: scale(1.1);
  opacity: 0.8;
}

.edit-icon:focus {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}
</style>
